"use client";

import React, { useMemo } from "react";
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
	UniqueIdentifier,
} from "@dnd-kit/core";
import {
	restrictToVerticalAxis,
	restrictToParentElement,
} from "@dnd-kit/modifiers";
import {
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
	useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
	flexRender,
	getCoreRowModel,
	Row,
	useReactTable,
} from "@tanstack/react-table";
import { GripVerticalIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { type AnyColumnDef } from "./data-table-types";
import { Table } from "./table";
import { Skeleton } from "./skeleton";

type ColumnMeta = {
	align?: "left" | "center" | "right";
	verticalAlign?: "top" | "center";
	truncate?: boolean;
};

type SortableRowProps<TData extends { id: string; index: number }> = {
	row: Row<TData>;
	isDisabled?: boolean;
	testId?: string;
};

function SortableItem<TData extends { id: string; index: number }>({
	row,
	isDisabled = false,
	testId,
}: SortableRowProps<TData>) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({
		id: row.original.id,
		disabled: isDisabled,
	});

	const style = {
		transform: CSS.Translate.toString(transform),
		transition,
	};

	const makeTestId = (id: string) => (testId ? `${testId}-${id}` : null);

	return (
		<Table.Row
			ref={setNodeRef}
			style={style}
			data-testid={makeTestId(`row-${row.original.index}`)}
			className={cn(isDragging ? "!border-y border-muted z-10" : "")}
		>
			<Table.Cell
				{...attributes}
				{...listeners}
				className="w-[30px]"
				style={{
					cursor: isDisabled ? "not-allowed" : isDragging ? "grabbing" : "grab",
				}}
				data-testid={makeTestId(`drag-handler-row-${row.original.index}`)}
			>
				<GripVerticalIcon
					className={cn(
						"size-4",
						isDisabled ? "text-neutral-200" : "text-neutral-500",
					)}
				/>
			</Table.Cell>
			{row.getVisibleCells().map((cell) => {
				const {
					align = "left",
					verticalAlign = "center",
					truncate = false,
				} = (cell.column.columnDef.meta as ColumnMeta) ?? {};
				const columnWidth = cell.column.getSize();

				return (
					<Table.Cell
						key={cell.id}
						data-testid={makeTestId(`cell-${cell.column.id}`)}
						style={{
							width: columnWidth !== 150 ? columnWidth : undefined,
						}}
						className={cn(
							"overflow-hidden",
							truncate && "truncate",
							align === "left" && "text-left",
							align === "center" && "text-center",
							align === "right" && "text-right",
							verticalAlign === "top" && "!align-top",
						)}
					>
						{flexRender(cell.column.columnDef.cell, cell.getContext())}
					</Table.Cell>
				);
			})}
		</Table.Row>
	);
}

type DraggableTableProps<TData extends { id: string; index: number }> = {
	data: TData[];
	columns: AnyColumnDef<TData>[];
	onDrag: (event: DragEndEvent) => void;
	isLoading?: boolean;
	isDisabled?: boolean;
	loadingRowCount?: number;
	"data-testid"?: string;
	emptyState?: React.ReactNode;
};

/**
 * Renders a sortable table with a dedicated drag-handle column.
 *
 * @template TData - Row data shape. Must include `{ id: string; index: number }`.
 * @param {DraggableTableProps<TData>} props
 * @param {TData[]} props.data - Table rows to render.
 * @param {AnyColumnDef<TData>[]} props.columns - TanStack column definitions for non-drag columns.
 * @param {(event: DragEndEvent) => void} props.onDrag - Drag completion handler. Receives the `DragEndEvent`.
 * @param {boolean} [props.isLoading=false] - Whether to show loading skeleton rows.
 * @param {boolean} [props.isDisabled=false] - Whether drag interactions are disabled.
 * @param {number} [props.loadingRowCount=3] - Number of skeleton rows to render while loading.
 * @param {string} [props.data-testid] - Optional base test id for generated row/cell ids.
 * @param {React.ReactNode} [props.emptyState=null] - Empty-state content shown when there are no rows.
 *
 * @example
 * ```tsx
 * <DraggableTable
 * 	data={rows}
 * 	columns={columns}
 * 	onDrag={(event) => handleReorder(event)}
 * 	isLoading={isLoading}
 * 	emptyState={<div>No rows</div>}
 * />
 * ```
 */
export function DraggableTable<TData extends { id: string; index: number }>({
	data,
	columns,
	onDrag,
	isLoading = false,
	isDisabled = false,
	loadingRowCount = 3,
	"data-testid": testId,
	emptyState = null,
}: DraggableTableProps<TData>) {
	// eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table returns a non-memoizable instance
	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
		getRowId: (row) => row.id,
	});

	const dataIds = useMemo<UniqueIdentifier[]>(
		() => data.map(({ id }) => id),
		[data],
	);

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);
	const rowModel = table.getRowModel();
	const headerGroups = table.getHeaderGroups();
	const makeTestId = (id: string) => (testId ? `${testId}-${id}` : null);

	return (
		<DndContext
			sensors={sensors}
			modifiers={[restrictToVerticalAxis, restrictToParentElement]}
			collisionDetection={closestCenter}
			onDragEnd={onDrag}
		>
			<Table>
				{headerGroups.map((headerGroup) => (
					<Table.Header key={headerGroup.id} data-testid={makeTestId("header")}>
						<Table.Column className="w-[30px]" />
						{headerGroup.headers.map((header) => {
							const { align = "left" } =
								(header.column.columnDef.meta as ColumnMeta) ?? {};
							const columnWidth = header.column.getSize();
							const headerContents = header.isPlaceholder
								? null
								: flexRender(
										header.column.columnDef.header,
										header.getContext(),
									);
							return (
								<Table.Column
									key={header.id}
									style={{
										width: columnWidth !== 150 ? columnWidth : undefined,
									}}
									className={cn(
										"overflow-hidden",
										align === "left" && "text-left",
										align === "center" && "text-center",
										align === "right" && "text-right",
									)}
								>
									{headerContents}
								</Table.Column>
							);
						})}
					</Table.Header>
				))}
				<Table.Body>
					{isLoading ? (
						<>
							{Array.from({ length: loadingRowCount }).map((_, index) => (
								<Table.Row key={`loading-${index}`}>
									<Table.Cell className="w-[30px]">
										<Skeleton className="my-[5px] flex h-[13px] items-center rounded-md" />
									</Table.Cell>
									{table.getAllColumns().map((column) => {
										const columnWidth = column.getSize();
										return (
											<Table.Cell
												key={column.id}
												style={{
													width: columnWidth !== 150 ? columnWidth : undefined,
												}}
											>
												<Skeleton className="my-[5px] flex h-[13px] items-center rounded-md" />
											</Table.Cell>
										);
									})}
								</Table.Row>
							))}
						</>
					) : (
						<SortableContext
							items={dataIds}
							strategy={verticalListSortingStrategy}
						>
							{rowModel.rows.length === 0 ? (
								<Table.Row>
									<Table.Cell
										colSpan={table.getVisibleFlatColumns().length + 1}
										className="h-[200px] text-center"
									>
										<div className="flex min-h-[200px] items-center justify-center">
											{emptyState}
										</div>
									</Table.Cell>
								</Table.Row>
							) : (
								rowModel.rows.map((row) => (
									<SortableItem
										key={row.id}
										row={row}
										testId={testId}
										isDisabled={isDisabled}
									/>
								))
							)}
						</SortableContext>
					)}
				</Table.Body>
			</Table>
		</DndContext>
	);
}
